import { Check, Minus, ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import SolutionMotif from "@/components/SolutionMotif";
import GearMark from "@/components/GearMark";
import { Reveal, SectionHeader } from "@/components/Reveal";
import { CTABand } from "@/components/CTA";
import { APPS } from "@/config";
import usePageMeta from "@/hooks/usePageMeta";

const ROWS = [
  ["Buyer benefits pitched from memory", "Buyer options presented on a page branded as yours"],
  ["Offers built by hand from tribal knowledge", "Offers drafted from the matching incentive playbook"],
  ["Offers released without a second look", "A human approval gate before anything goes out"],
  ["Status chased across email threads", "One timeline per deal, tracked to acceptance"],
];

function AppHubCard({ app, index }) {
  return (
    <Reveal delay={index * 0.08} className="h-full">
      <article
        data-testid={`hub-card-${app.id}`}
        className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-line bg-white transition-all duration-500 hover:border-vermillion/40 hover:shadow-[0_40px_80px_-50px_rgba(214,53,10,0.5)]"
      >
        <div className="relative">
          <SolutionMotif variant={index === 0 ? "amplify" : "sync"} />
        </div>
        <div className="flex flex-1 flex-col p-8 lg:p-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-inksoft">
            App {index + 1} of 2 — {app.id}
          </p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-[-0.02em] text-ink lg:text-3xl">
            {app.name}
          </h2>
          <p className="mt-4 inline-flex w-fit items-center gap-2.5 rounded-full border border-line bg-paper px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-inksoft">
            <span className="h-1.5 w-1.5 rotate-45 bg-vermillion" />
            {app.loginLabel}
          </p>
          <p className="mt-4 leading-relaxed text-inksoft">{app.tagline}</p>
          <div className="mt-auto flex flex-wrap gap-3 border-t border-line pt-7">
            <Link
              to={app.path}
              data-testid={`hub-explore-${app.id}`}
              className="group/link inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-sm font-semibold text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-paper"
            >
              Explore
              <ArrowRight
                size={15}
                className="transition-transform duration-300 group-hover/link:translate-x-1"
              />
            </Link>
            <a
              href={app.loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`hub-signin-${app.id}`}
              className="group inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-paper transition-all duration-300 hover:bg-vermillion"
            >
              Sign in
              <ArrowUpRight
                size={15}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function Solutions() {
  usePageMeta(
    "Solutions — Transactr",
    "Two apps, one autonomous revenue motion: The Next Level Marketplace Motion and Private Offer Autopilot. Explore each, sign in, or book a demo."
  );
  return (
    <>
      <section className="relative overflow-hidden">
        <div className="blueprint-grid pointer-events-none absolute inset-0 opacity-60" />
        <div className="container-t relative pb-16 pt-36 lg:pb-24 lg:pt-44">
          <SectionHeader
            eyebrow="Solutions"
            title={
              <>
                Two apps. One autonomous{" "}
                <em className="font-accent font-medium italic text-vermillion">revenue motion</em>.
              </>
            }
            sub="One front door, two motions: explore each app, then sign in directly or book a demo with the founder."
          />
        </div>
      </section>

      <section className="container-t pb-24 lg:pb-32" data-testid="solutions-hub">
        <div className="grid gap-6 lg:grid-cols-2">
          {APPS.map((a, i) => (
            <AppHubCard key={a.id} app={a} index={i} />
          ))}
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-parchment/60 py-24 lg:py-32" data-testid="comparison-section">
        <div className="container-t">
          <SectionHeader
            eyebrow="The difference"
            title="Manual co-sell vs. autonomous."
            sub="The same motion, run two different ways."
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            <Reveal className="h-full">
              <div className="h-full rounded-3xl border border-line bg-white/60 p-8 lg:p-10">
                <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-inksoft">
                  <Minus size={14} /> By hand
                </p>
                <ul className="mt-7">
                  {ROWS.map((r) => (
                    <li key={r[0]} className="flex items-start gap-3 border-t border-line py-4 text-sm leading-relaxed text-inksoft">
                      <Minus size={14} className="mt-0.5 shrink-0 text-inksoft/50" />
                      {r[0]}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="h-full">
              <div className="relative h-full overflow-hidden rounded-3xl border border-vermillion/30 bg-white p-8 shadow-[0_40px_80px_-55px_rgba(214,53,10,0.55)] lg:p-10">
                <GearMark size={120} className="pointer-events-none absolute -right-8 -top-8 animate-spin-slower opacity-[0.07]" />
                <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-vermillion">
                  <Check size={14} /> With Transactr
                </p>
                <ul className="mt-7">
                  {ROWS.map((r) => (
                    <li key={r[1]} className="flex items-start gap-3 border-t border-vermillion/15 py-4 text-sm font-medium leading-relaxed text-ink">
                      <Check size={14} className="mt-0.5 shrink-0 text-vermillion" />
                      {r[1]}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
